export type IsoWeekOrMonth = IsoWeek | IsoMonth;

export const ISO_TYPE_WEEK = 'week';
export const ISO_TYPE_MONTH = 'month';

export class IsoWeek {
	readonly type = ISO_TYPE_WEEK;
	constructor(
		readonly value: number,
		readonly year: number
	) {}
}

export class IsoMonth {
	readonly type = ISO_TYPE_MONTH;
	constructor(
		readonly value: number,
		readonly year: number
	) {}

	isEqual(that: IsoMonth) {
		return this.year === that.year && this.value === that.value;
	}

	isAfter(that: IsoMonth) {
		return this.year > that.year || (this.year === that.year && this.value > that.value);
	}

	isAfterOrEqual(that: IsoMonth) {
		return this.year > that.year || (this.year === that.year && this.value >= that.value);
	}

	isBefore(that: IsoMonth) {
		return this.year < that.year || (this.year === that.year && this.value < that.value);
	}

	isBeforeOrEqual(that: IsoMonth) {
		return this.year < that.year || (this.year === that.year && this.value <= that.value);
	}

	compareWith(that: IsoMonth) {
		const diffYear = this.year - that.year;
		return diffYear === 0 ? this.value - that.value : diffYear;
	}

	plus(monthCount: number) {
		let { year } = this;
		let month = this.value + monthCount;
		while (month > 12) {
			month -= 12;
			year += 1;
		}
		while (month < 1) {
			month += 12;
			year -= 1;
		}
		return new IsoMonth(month, year);
	}

	minus(monthCount: number) {
		return this.plus(-monthCount);
	}
}

export class PeriodIsoMonth {
	constructor(
		readonly start: IsoMonth,
		readonly end: IsoMonth
	) {}

	monthCount() {
		return 12 - this.start.value + this.end.value + 1 + ((this.end.year - this.start.year - 1) * 12);
	}

	isOneYear() {
		return this.monthCount() === 12 && this.start.value === 1;
	}

	isOneSemester() {
		return this.monthCount() === 6 && this.start.value % 6 === 1;
	}

	isOneQuarter() {
		return this.monthCount() === 3 && this.start.value % 3 === 1;
	}

	contains(month: IsoMonth) {
		return this.start.isBeforeOrEqual(month) && month.isBeforeOrEqual(this.end);
	}

	containsMonth(year: number, month: number) {
		return this.contains(new IsoMonth(month, year));
	}

	containsQuarter(year: number, quarter: number) {
		return this.contains(new IsoMonth((quarter * 3) + 1, year))
			&& this.contains(new IsoMonth((quarter * 3) + 3, year));
	}

	containsSemester(year: number, semester: number) {
		return this.contains(new IsoMonth((semester * 6) + 1, year))
			&& this.contains(new IsoMonth((semester * 6) + 6, year));
	}

	containsYear(year: number) {
		return this.contains(new IsoMonth(1, year))
			&& this.contains(new IsoMonth(12, year));
	}

	extendFromTo(month: IsoMonth) {
		if (month.isBefore(this.start)) {
			return new PeriodIsoMonth(month, this.end);
		}
		if (month.isAfter(this.end)) {
			return new PeriodIsoMonth(this.start, month);
		}
		return this;
	}

	compareWith(that: PeriodIsoMonth) {
		const diffStart = this.start.compareWith(that.start);
		return diffStart === 0 ? this.end.compareWith(that.end) : diffStart;
	}
}
